import './index.css'
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { AlertCircle, CheckCircle2, Info, TriangleAlert, X } from 'lucide-react'

export type ToastType = 'success' | 'error' | 'warning' | 'info'

interface ToastItem {
  id: number
  type: ToastType
  message: string
  duration: number
}

interface ToastOptions {
  duration?: number
}

interface ToastApi {
  show: (type: ToastType, message: string, options?: ToastOptions) => void
  success: (message: string, options?: ToastOptions) => void
  error: (message: string, options?: ToastOptions) => void
  warning: (message: string, options?: ToastOptions) => void
  info: (message: string, options?: ToastOptions) => void
}

const ToastContext = createContext<ToastApi | null>(null)

const fallbackToast: ToastApi = {
  show: () => undefined,
  success: () => undefined,
  error: () => undefined,
  warning: () => undefined,
  info: () => undefined,
}

const ICONS = {
  success: CheckCircle2,
  error: AlertCircle,
  warning: TriangleAlert,
  info: Info,
}

function titleFor(type: ToastType): string {
  if (type === 'success') return 'Tudo certo'
  if (type === 'error') return 'Algo deu errado'
  if (type === 'warning') return 'Atenção'
  return 'Informação'
}

export function getToastErrorMessage(err: unknown, fallback = 'Não foi possível concluir a ação. Tente novamente.'): string {
  if (err instanceof Error && err.message) return err.message
  if (typeof err === 'string' && err.trim()) return err
  if (err && typeof err === 'object' && 'message' in err) {
    const message = (err as { message?: unknown }).message
    if (typeof message === 'string' && message.trim()) return message
  }
  return fallback
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([])
  const idRef = useRef(0)

  const dismiss = useCallback((id: number) => {
    setToasts(prev => prev.filter(toast => toast.id !== id))
  }, [])

  const show = useCallback((type: ToastType, message: string, options?: ToastOptions) => {
    const trimmed = message.trim()
    if (!trimmed) return
    const id = ++idRef.current
    setToasts(prev => [
      ...prev.slice(-3),
      { id, type, message: trimmed, duration: options?.duration ?? (type === 'error' ? 6500 : 4200) },
    ])
  }, [])

  const api = useMemo<ToastApi>(() => ({
    show,
    success: (message, options) => show('success', message, options),
    error: (message, options) => show('error', message, options),
    warning: (message, options) => show('warning', message, options),
    info: (message, options) => show('info', message, options),
  }), [show])

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="bc-toast-region" aria-live="polite" aria-atomic="false">
        {toasts.map(toast => (
          <ToastCard key={toast.id} toast={toast} onDismiss={() => dismiss(toast.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  )
}

function ToastCard({ toast, onDismiss }: { toast: ToastItem; onDismiss: () => void }) {
  const Icon = ICONS[toast.type]

  useEffect(() => {
    const timer = window.setTimeout(onDismiss, toast.duration)
    return () => window.clearTimeout(timer)
  }, [onDismiss, toast.duration])

  return (
    <div className={`bc-toast-card bc-toast-card--${toast.type}`} role={toast.type === 'error' ? 'alert' : 'status'}>
      <div className="bc-toast-card__icon" aria-hidden="true">
        <Icon size={18} />
      </div>
      <div className="bc-toast-card__body">
        <span className="bc-toast-card__title">{titleFor(toast.type)}</span>
        <p className="bc-toast-card__message">{toast.message}</p>
      </div>
      <button className="bc-toast-card__close" onClick={onDismiss} aria-label="Fechar notificação">
        <X size={16} />
      </button>
    </div>
  )
}

export function useToast(): ToastApi {
  return useContext(ToastContext) ?? fallbackToast
}
